import { useEffect, useState, useCallback } from 'react';
import { Documentation, FormAddDocument } from 'types/documents';
import { createDocumentation, getDocumentations } from 'services/documentation';
import { SIZE_DOCUMENTATION_PAGE } from 'const/paging';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';

export const useFetchDocumentationWeb = () => {
  const [data, setData] = useState<Documentation[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const { setFlash } = useFlash();
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  // Fetch first page of documentation
  const getOptionDocumentation = useCallback(async () => {
    const params = {
      page: 1,
      size: SIZE_DOCUMENTATION_PAGE
    };
    const result = await getDocumentations(params);
    setData(result.documentations);
    if (result.total < SIZE_DOCUMENTATION_PAGE) {
      setHasMore(false);
    } else {
      setHasMore(true);
      setPage(2);
    }
  }, []);

  // Fetch more for pagination
  const handleFetchMoreDocumentation = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    try {
      const params = {
        page,
        size: SIZE_DOCUMENTATION_PAGE
      };
      setIsLoadingMore(true);
      const result = await getDocumentations(params);
      setData((prev) => [...prev, ...result.documentations]);
      if (result.total < SIZE_DOCUMENTATION_PAGE * page) {
        setHasMore(false);
      } else {
        setPage((prev) => prev + 1);
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, page]);

  useEffect(() => {
    getOptionDocumentation();
  }, [getOptionDocumentation]);

  const handleAddDocument = useCallback(
    async (value: FormAddDocument) => {
      if (!value?.category || !value?.clientIds || value.clientIds.length === 0) return;
      const formData = new FormData();
      const blob = new Blob([value.file], { type: value.file?.type });
      formData.append('file', blob, value.file?.name);
      formData.append('category', value?.category);
      formData.append('name', value.name);
      [...value.clientIds]
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .forEach((id) => {
          formData.append('clientIds[]', id);
        });
      await createDocumentation(formData);
      setFlash({ type: 'success', message: 'Create documentation successfully' });
      await getOptionDocumentation();
    },
    [getOptionDocumentation, setFlash]
  );

  const { asyncCallback: asyncAddDocument } = useAsyncCallback(handleAddDocument, []);

  return {
    data,
    getOptionDocumentation,
    handleFetchMoreDocumentation,
    isLoadingMore,
    hasMore,
    asyncAddDocument
  };
};
